import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import { useToast } from '../../ToastContext';
import { useAuth } from '../../AuthContext';

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        checked ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
      }`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-4' : 'translate-x-1'
      }`} />
    </button>
  );
}

function SectionHeader({ label, count, color = 'slate' }) {
  const colors = {
    blue:  'text-blue-600 dark:text-blue-400',
    slate: 'text-slate-500 dark:text-slate-400',
    red:   'text-red-600 dark:text-red-400',
  };
  return (
    <div className="flex items-center gap-3">
      <span className={`text-xs font-semibold uppercase tracking-wider ${colors[color]}`}>{label}</span>
      <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">{count}</span>
      <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

function UserTable({ users, appUser, saving, deleting, onUpdate, onDelete, emptyText }) {
  return (
    <div className="card overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-800/50">
            <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">User</th>
            <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Account</th>
            <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Location</th>
            <th className="py-2.5 px-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Acct Admin</th>
            <th className="py-2.5 px-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Platform Admin</th>
            <th className="py-2.5 px-4" />
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const isSelf = u.id === appUser?.id;
            const isDeactivated = u.fname === '[deleted]';
            return (
              <tr key={u.id} className={`border-t border-slate-100 dark:border-slate-800 ${isDeactivated ? 'opacity-50' : ''}`}>
                <td className="py-3 px-4">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {[u.fname, u.lname].filter(Boolean).join(' ') || '—'}
                    {isSelf && <span className="ml-1.5 text-xs text-slate-400">(you)</span>}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{u.email || '—'}</p>
                </td>
                <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                  {u.account_name || <span className="text-slate-400">—</span>}
                </td>
                <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                  {u.location_name || <span className="text-slate-400">—</span>}
                </td>
                <td className="py-3 px-4 text-center">
                  <Toggle
                    checked={!!u.is_admin}
                    disabled={isSelf || saving === u.id || isDeactivated}
                    onChange={val => onUpdate(u.id, { is_admin: val })}
                  />
                </td>
                <td className="py-3 px-4 text-center">
                  <Toggle
                    checked={!!u.is_platform_admin}
                    disabled={isSelf || saving === u.id || isDeactivated}
                    onChange={val => onUpdate(u.id, { is_platform_admin: val })}
                  />
                </td>
                <td className="py-3 px-4 text-right">
                  {!isSelf && !isDeactivated && (
                    <button
                      onClick={() => onDelete(u)}
                      disabled={deleting === u.id}
                      title="Delete user"
                      className="p-1.5 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {deleting === u.id
                        ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" />
                        : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )
                      }
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {users.length === 0 && (
            <tr>
              <td colSpan={6} className="py-8 text-center text-sm text-slate-400">{emptyText}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function Users() {
  const { appUser } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    api.get('/admin/users').then(setUsers).finally(() => setLoading(false));
  }, []);

  const update = useCallback(async (userId, patch) => {
    setSaving(userId);
    try {
      const updated = await api.put(`/admin/users/${userId}`, patch);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updated } : u));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(null);
    }
  }, [toast]);

  const deleteUser = useCallback(async (user) => {
    const name = [user.fname, user.lname].filter(Boolean).join(' ') || user.email || `User #${user.id}`;
    if (!window.confirm(`Delete ${name}?\n\nThis removes their login access. If they have transaction history their record will be deactivated (email cleared) rather than fully deleted.`)) return;
    setDeleting(user.id);
    try {
      const result = await api.del(`/admin/users/${user.id}`);
      if (result?.deactivated) {
        setUsers(prev => prev.map(u => u.id === user.id
          ? { ...u, email: null, fname: '[deleted]', lname: '' }
          : u
        ));
        toast.success('User deactivated — login removed, email cleared (transaction history preserved)');
      } else {
        setUsers(prev => prev.filter(u => u.id !== user.id));
        toast.success('User deleted');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeleting(null);
    }
  }, [toast]);

  const match = u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.email?.toLowerCase().includes(q) ||
      u.fname?.toLowerCase().includes(q) ||
      u.lname?.toLowerCase().includes(q) ||
      u.account_name?.toLowerCase().includes(q)
    );
  };

  const filtered    = users.filter(match);
  const active      = filtered.filter(u => u.fname !== '[deleted]');
  const deactivated = filtered.filter(u => u.fname === '[deleted]');

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-8">
      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by name, email, or account…"
        className="w-full max-w-sm text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400"
      />

      <div className="space-y-3">
        <SectionHeader label="Active" count={active.length} color="blue" />
        <UserTable
          users={active}
          appUser={appUser}
          saving={saving}
          deleting={deleting}
          onUpdate={update}
          onDelete={deleteUser}
          emptyText={search ? 'No matching active users' : 'No active users'}
        />
      </div>

      <div className="space-y-3">
        <SectionHeader label="Deactivated" count={deactivated.length} color="red" />
        <UserTable
          users={deactivated}
          appUser={appUser}
          saving={saving}
          deleting={deleting}
          onUpdate={update}
          onDelete={deleteUser}
          emptyText="No deactivated users"
        />
      </div>
    </div>
  );
}
